import React, { useState, useEffect, useRef } from 'react';

/**
 * AnimatedCounter component that animates numbers from 0 to target value
 * @param {number|string} target - Target value to count to (e.g., 98, "10,000+")
 * @param {number} duration - Animation duration in milliseconds (default: 2000)
 * @param {string} suffix - Optional suffix (e.g., "%", "+")
 */
const AnimatedCounter = ({ target, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);

  // Parse target value (handle cases like "10,000+", "98%", "24/7", "50+")
  const parseTarget = (value) => {
    if (typeof value === 'number') return value;
    
    // Extract numeric value
    const numericValue = value.replace(/[^0-9]/g, '');
    return parseInt(numericValue) || 0;
  };

  const targetValue = parseTarget(target);

  // Intersection Observer to trigger animation when element is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, []);

  // Animate counter when visible
  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const startValue = 0;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - percentage, 3);
      const currentCount = Math.floor(startValue + (targetValue - startValue) * easeOut);

      setCount(currentCount);

      if (percentage < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(targetValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, targetValue, duration]);

  // Format display value
  const formatValue = () => {
    const originalTarget = target.toString();
    
    // Handle special cases
    if (originalTarget.includes('/')) {
      // For "24/7" format
      return originalTarget;
    }
    
    if (originalTarget.includes(',')) {
      // For "10,000+" format - add comma back
      return count.toLocaleString() + (originalTarget.includes('+') ? '+' : '');
    }
    
    // For "98%" or "50+" format
    return count + suffix;
  };

  return (
    <span ref={counterRef} className="inline-block">
      {formatValue()}
    </span>
  );
};

export default AnimatedCounter;
